/**
 * Composant CreateQuestionnaireDialog pour la page Satisfaction
 * Boîte de dialogue avancée pour créer et envoyer un questionnaire à plusieurs destinataires
 */

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, FileText, UserCheck, X, Users } from 'lucide-react';

// Questions par défaut selon le type
export const QUESTIONS_PAR_DEFAUT = {
  stagiaire_diplome: [
    { id: 'q1', texte: 'Comment évaluez-vous la qualité globale de la formation ?', type: 'rating' },
    { id: 'q2', texte: 'La formation a-t-elle répondu à vos attentes ?', type: 'rating' },
    { id: 'q3', texte: 'Comment évaluez-vous les compétences des formateurs ?', type: 'rating' },
    { id: 'q4', texte: 'Comment évaluez-vous les moyens matériels mis à disposition ?', type: 'rating' },
    { id: 'q5', texte: 'Recommanderiez-vous cette formation ?', type: 'rating' },
    { id: 'q6', texte: 'Avez-vous des suggestions d\'amélioration ?', type: 'text' }
  ],
  entreprise: [
    { id: 'q1', texte: 'Comment évaluez-vous la préparation du stagiaire ?', type: 'rating' },
    { id: 'q2', texte: 'Le stagiaire a-t-il répondu à vos attentes ?', type: 'rating' },
    { id: 'q3', texte: 'Comment évaluez-vous la communication avec le centre ?', type: 'rating' },
    { id: 'q4', texte: 'Seriez-vous prêt à accueillir d\'autres stagiaires ?', type: 'rating' },
    { id: 'q5', texte: 'Commentaires et suggestions', type: 'text' }
  ],
  tuteur: [
    { id: 'q1', texte: 'Comment évaluez-vous l\'accompagnement du centre ?', type: 'rating' },
    { id: 'q2', texte: 'Les outils de suivi sont-ils adaptés ?', type: 'rating' },
    { id: 'q3', texte: 'Comment évaluez-vous la communication avec les formateurs ?', type: 'rating' },
    { id: 'q4', texte: 'Suggestions d\'amélioration', type: 'text' }
  ],
  formateur: [
    { id: 'q1', texte: 'Comment évaluez-vous les conditions de travail ?', type: 'rating' },
    { id: 'q2', texte: 'Les moyens pédagogiques sont-ils adaptés ?', type: 'rating' },
    { id: 'q3', texte: 'Comment évaluez-vous la coordination avec l\'équipe ?', type: 'rating' },
    { id: 'q4', texte: 'Le planning de formation est-il satisfaisant ?', type: 'rating' },
    { id: 'q5', texte: 'Suggestions d\'amélioration', type: 'text' }
  ],
  financeur: [
    { id: 'q1', texte: 'Comment évaluez-vous la qualité des documents fournis ?', type: 'rating' },
    { id: 'q2', texte: 'Comment évaluez-vous la réactivité du centre ?', type: 'rating' },
    { id: 'q3', texte: 'Comment évaluez-vous le respect des engagements ?', type: 'rating' },
    { id: 'q4', texte: 'Commentaires', type: 'text' }
  ]
};

export const CreateQuestionnaireDialog = ({
  open,
  onOpenChange,
  sendData,
  handleTypeChange,
  modelesPersonnalises,
  loadModelePersonnalise,
  deleteModelePersonnalise,
  customQuestions,
  setCustomQuestions,
  newQuestion,
  setNewQuestion,
  addCustomQuestion,
  removeCustomQuestion,
  savedModeles,
  resetToDefault,
  saveModele,
  destinataireFilter,
  setDestinataireFilter,
  destinataires,
  selectedDestinataires,
  toggleDestinataire,
  selectAllDestinataires,
  clearSelectedDestinataires,
  sending,
  onSendMultiple,
  onClose
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-questionnaire-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-600" />
            Créer et envoyer un questionnaire
          </DialogTitle>
          <DialogDescription>
            Sélectionnez les destinataires et personnalisez les questions si nécessaire
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Colonne gauche : Type et questions */}
          <div className="space-y-4">
            <div>
              <Label>Type de questionnaire</Label>
              <Select value={sendData.type} onValueChange={handleTypeChange}>
                <SelectTrigger data-testid="select-questionnaire-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stagiaire_diplome">Questionnaire diplômé</SelectItem>
                  <SelectItem value="entreprise">Questionnaire entreprise</SelectItem>
                  <SelectItem value="tuteur">Questionnaire tuteur</SelectItem>
                  <SelectItem value="formateur">Questionnaire formateur</SelectItem>
                  <SelectItem value="financeur">Questionnaire financeur</SelectItem>
                  <SelectItem value="personnalise">Questionnaire personnalisé</SelectItem>
                  {modelesPersonnalises.length > 0 && (
                    <>
                      {modelesPersonnalises.map(modele => (
                        <SelectItem key={modele.id} value={modele.id}>
                          ⭐ {modele.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Liste des modèles personnalisés */}
            {modelesPersonnalises.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-medium text-amber-700 mb-2">Mes modèles personnalisés :</p>
                <div className="flex flex-wrap gap-2">
                  {modelesPersonnalises.map(modele => (
                    <div key={modele.id} className="flex items-center gap-1 bg-white px-2 py-1 rounded border text-sm">
                      <span 
                        className="cursor-pointer hover:text-blue-600"
                        onClick={() => loadModelePersonnalise(modele)}
                      >
                        {modele.name}
                      </span>
                      <button 
                        onClick={() => deleteModelePersonnalise(modele.id)}
                        className="text-red-400 hover:text-red-600 ml-1"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Questions du questionnaire */}
            <div className="border rounded-lg p-4 bg-slate-50 max-h-[400px] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Questions du questionnaire
                </h4>
                <div className="flex gap-2">
                  {customQuestions.length > 0 && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={resetToDefault}
                      >
                        Réinitialiser
                      </Button>
                      <Button 
                        size="sm"
                        onClick={saveModele}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Enregistrer modèle
                      </Button>
                    </>
                  )}
                  {sendData.type !== 'personnalise' && customQuestions.length === 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const defaultQ = QUESTIONS_PAR_DEFAUT[sendData.type] || [];
                        setCustomQuestions([...defaultQ]);
                      }}
                    >
                      Charger questions par défaut
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Indicateur de modèle sauvegardé */}
              {savedModeles[sendData.type] && customQuestions.length === 0 && (
                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                  ✓ Modèle personnalisé sauvegardé pour ce type
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="ml-2 text-green-700 p-0 h-auto"
                    onClick={() => setCustomQuestions([...savedModeles[sendData.type]])}
                  >
                    Charger
                  </Button>
                </div>
              )}
              
              {/* Questions par défaut si aucune personnalisation */}
              {customQuestions.length === 0 && sendData.type !== 'personnalise' && (
                <div className="space-y-2 mb-4">
                  <p className="text-xs text-slate-500 mb-2">Questions par défaut (cliquez sur "Charger" pour les modifier) :</p>
                  {(QUESTIONS_PAR_DEFAUT[sendData.type] || []).map((q, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-white/50 rounded border border-dashed text-sm text-slate-600">
                      <span className="flex-1">{q.texte}</span>
                      <Badge variant="outline" className="text-xs">{q.type === 'rating' ? 'Étoiles' : 'Texte'}</Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Questions personnalisées ou modifiées */}
              {customQuestions.length > 0 && (
                <div className="space-y-2 mb-4">
                  {customQuestions.map((q, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded border">
                      <Input
                        value={q.texte}
                        onChange={(e) => {
                          const updated = [...customQuestions];
                          updated[idx] = {...q, texte: e.target.value};
                          setCustomQuestions(updated);
                        }}
                        className="flex-1 text-sm"
                      />
                      <Select 
                        value={q.type} 
                        onValueChange={(v) => {
                          const updated = [...customQuestions];
                          updated[idx] = {...q, type: v};
                          setCustomQuestions(updated);
                        }}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rating">Étoiles</SelectItem>
                          <SelectItem value="text">Texte</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeCustomQuestion(idx)}
                        className="text-red-500 h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Ajouter une question */}
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-slate-500 mb-2">Ajouter une question :</p>
                <div className="flex gap-2">
                  <Input
                    value={newQuestion.texte}
                    onChange={(e) => setNewQuestion({...newQuestion, texte: e.target.value})}
                    placeholder="Nouvelle question..."
                    className="flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && addCustomQuestion()}
                    data-testid="input-new-question"
                  />
                  <Select value={newQuestion.type} onValueChange={(v) => setNewQuestion({...newQuestion, type: v})}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rating">Étoiles</SelectItem>
                      <SelectItem value="text">Texte</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addCustomQuestion} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-question">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite : Sélection destinataires */}
          <div className="space-y-4">
            <div>
              <Label>Catégorie de destinataires</Label>
              <Select value={destinataireFilter} onValueChange={setDestinataireFilter}>
                <SelectTrigger data-testid="select-destinataire-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stagiaires">Stagiaires ({destinataires.stagiaires?.length || 0})</SelectItem>
                  <SelectItem value="formateurs">Formateurs ({destinataires.formateurs?.length || 0})</SelectItem>
                  <SelectItem value="entreprises">Entreprises ({destinataires.entreprises?.length || 0})</SelectItem>
                  <SelectItem value="tuteurs">Tuteurs ({destinataires.tuteurs?.length || 0})</SelectItem>
                  <SelectItem value="moniteurs">Moniteurs VNM ({destinataires.moniteurs?.length || 0})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllDestinataires} data-testid="btn-select-all">
                <UserCheck className="h-4 w-4 mr-1" />
                Tout sélectionner
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelectedDestinataires} data-testid="btn-deselect-all">
                <X className="h-4 w-4 mr-1" />
                Tout désélectionner
              </Button>
            </div>

            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              {(destinataires[destinataireFilter] || []).length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Aucun destinataire dans cette catégorie</p>
                </div>
              ) : (
                <div className="divide-y">
                  {(destinataires[destinataireFilter] || []).map((dest, idx) => {
                    const isSelected = selectedDestinataires.some(d => d.email === dest.email);
                    return (
                      <div 
                        key={idx}
                        onClick={() => toggleDestinataire(dest)}
                        className={`p-3 cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`destinataire-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            onChange={() => {}}
                            className="h-4 w-4 text-blue-600"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{dest.nom || 'Sans nom'}</p>
                            <p className="text-xs text-slate-500">{dest.email}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedDestinataires.length > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>{selectedDestinataires.length}</strong> destinataire(s) sélectionné(s)
                </p>
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button 
            onClick={onSendMultiple} 
            disabled={sending || selectedDestinataires.length === 0 || (sendData.type === 'personnalise' && customQuestions.length === 0)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="btn-send-multiple"
          >
            {sending ? 'Envoi en cours...' : `Envoyer à ${selectedDestinataires.length} destinataire(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateQuestionnaireDialog;
